import type { CanvasSectionNodeProps } from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import type { ReferencedEntity } from "@cohesive/api-client";
import { BaseEntityCardQuickChat } from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import {
  NodeResizer,
  useReactFlow,
  useStore,
  useViewport,
} from "@xyflow/react";
import { AnimatePresence } from "motion/react";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useParams } from "react-router-dom";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import { useCanvasSectionPreview } from "../context/CanvasSectionPreviewContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useApplySectionMembership } from "../hooks/useApplySectionMembership";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { shallowEqualReferencedEntities } from "../utils/reactFlowSelectors";
import {
  computeResizeToFitLayout,
  computeSectionLayoutByMode,
  type SectionLayoutMode,
} from "../utils/sectionChildLayout";
import { resolveSectionFrameDimensions } from "../utils/sectionLegacySizing";
import {
  boundsFromRect,
  getAdoptionCandidates,
} from "../utils/sectionMembership";
import {
  getSectionBorderColor,
  SectionFloatingToolbar,
} from "./SectionFloatingToolbar";
import {
  DEFAULT_SECTION_COLOR,
  SECTION_COLORS,
} from "./shared/CanvasColorPalettes";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

// Title-pill clamping at zoom-out. Matches TextNoteNode:
// outer px-2.5 + input padding (no chevron here).
const PILL_CHROME_PX = 24;
const TITLE_DEFAULT_MAX_PX = 175;
const PILL_NODE_WIDTH_RATIO = 0.6;
const MIN_PILL_ZOOM = 0.6;
const MIN_SECTION_SIZE = 96;

function normalizeSectionColor(raw: string): string {
  return SECTION_COLORS.some((c) => c.value === raw || c.swatchHex === raw)
    ? raw
    : DEFAULT_SECTION_COLOR;
}

function getChildDisplayTitle(node: Node): string {
  const d = node.data as { title?: string; content?: string };
  if (typeof d?.title === "string" && d.title.trim()) return d.title.trim();
  if (typeof d?.content === "string" && d.content.trim()) {
    return d.content.trim().slice(0, 48);
  }
  return node.type ?? "Item";
}

function childNodesToReferencedEntities(children: Node[]): ReferencedEntity[] {
  return children.map((child) => {
    const entity = (
      child.data as {
        entity?: {
          id?: string;
          type?: string;
          name?: string;
          platform?: string;
        };
      }
    )?.entity;
    if (entity) {
      return {
        entity_id: String(entity.id ?? child.id),
        entity_type: entity.type ?? "entity",
        name: getChildDisplayTitle(child) || entity.name || "Untitled",
        platform: entity.platform ?? "cohesive",
      };
    }
    return {
      entity_id: child.id,
      entity_type:
        child.type === "textNote"
          ? "canvas_text_note"
          : child.type === "canvasText"
            ? "canvas_text_label"
            : "canvas_section_child",
      name: getChildDisplayTitle(child),
      platform: "cohesive",
    };
  });
}

export const SectionNode = memo(function SectionNode({
  id,
  data,
  selected,
  dragging,
  width = 300,
  height = 200,
  positionAbsoluteX = 0,
  positionAbsoluteY = 0,
}: CanvasSectionNodeProps) {
  const canvas = useCanvasApi();
  const { canEdit, batchUpdateNodeParents } = canvas;
  const { setToolbarPopoverOpen, isResizingRef } = useCanvasUi();
  const { sectionPreview, setSectionPreview } = useCanvasSectionPreview();
  const { applySectionMembership } = useApplySectionMembership();
  const { dispatchProcess } = useCanvasProcess();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();
  const { getNodes, getInternalNode } = useReactFlow();
  const isPreviewTarget = sectionPreview?.targetSectionId === id;

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);
  const toolbarMouseDownRef = useRef(false);

  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  const rawSectionColor = data?.sectionColor ?? DEFAULT_SECTION_COLOR;
  const sectionColor = normalizeSectionColor(rawSectionColor);
  const noFill = data?.noFill ?? true;
  const borderStyle = data?.borderStyle ?? "solid";
  const borderWidth = data?.borderWidth ?? 2;

  const childEntities = useStore(
    useCallback(
      (s) => {
        const children = s.nodes.filter((n) => n.parentId === id);
        return childNodesToReferencedEntities(children);
      },
      [id],
    ),
    shallowEqualReferencedEntities,
  );

  const title = data?.title ?? "Section";
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const titleMeasureRef = useRef<HTMLSpanElement>(null);
  const [titleWidth, setTitleWidth] = useState(60);
  const resizeOriginRef = useRef<{ x: number; y: number } | null>(null);

  const updateSectionResizePreview = useCallback(
    (params: { x: number; y: number; width: number; height: number }) => {
      const internal = getInternalNode(id);
      const abs = internal?.internals?.positionAbsolute;
      const origin =
        abs && typeof abs.x === "number" && typeof abs.y === "number"
          ? { x: abs.x, y: abs.y }
          : { x: params.x, y: params.y };
      const sectionBounds = boundsFromRect(origin, params.width, params.height);
      const candidates = getAdoptionCandidates(getNodes(), sectionBounds, {
        excludeSectionId: id,
        getInternalNode,
      });
      setSectionPreview({
        memberIds: new Set(candidates.map((n) => n.id)),
        targetSectionId: id,
        mode: "resize",
      });
    },
    [id, getNodes, getInternalNode, setSectionPreview],
  );

  const isEditingTitleRef = useRef(false);
  isEditingTitleRef.current = isEditingTitle;

  useEffect(() => {
    if (!isEditingTitleRef.current) setLocalTitle(title);
  }, [title]);

  useLayoutEffect(() => {
    if (titleMeasureRef.current) {
      setTitleWidth(titleMeasureRef.current.offsetWidth);
    }
  }, [localTitle]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      setToolbarPopoverOpen(open);
    },
    [setToolbarPopoverOpen],
  );

  const handleStartEditingTitle = useCallback(() => {
    if (!canEdit) return;
    setIsEditingTitle(true);
  }, [canEdit]);

  const commitTitle = useCallback(
    (value: string) => {
      const trimmed = value.trim() || "Section";
      setLocalTitle(trimmed);
      canvas.updateNodeData(id, { title: trimmed });
    },
    [id, canvas],
  );

  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    commitTitle(localTitle);
  }, [localTitle, commitTitle]);

  const handleTitleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.currentTarget.blur();
      } else if (e.key === "Escape") {
        setLocalTitle(title);
        setIsEditingTitle(false);
      }
      e.stopPropagation();
    },
    [title],
  );

  const handleColorChange = useCallback(
    (color: string) => {
      canvas.updateNodeData(id, { sectionColor: color });
    },
    [id, canvas],
  );

  const handleNoFillChange = useCallback(
    (nextNoFill: boolean) => {
      canvas.updateNodeData(id, { noFill: nextNoFill });
    },
    [id, canvas],
  );

  const handleBorderStyleChange = useCallback(
    (style: "solid" | "dashed" | "dotted") => {
      canvas.updateNodeData(id, { borderStyle: style });
    },
    [id, canvas],
  );

  const handleBorderWidthChange = useCallback(
    (w: number) => {
      canvas.updateNodeData(id, { borderWidth: w });
    },
    [id, canvas],
  );

  const handleResizeToFit = useCallback(() => {
    const sectionNode = getNodes().find((n) => n.id === id);
    if (!sectionNode) return;
    const children = getNodes().filter((n) => n.parentId === id);
    const layout = computeResizeToFitLayout(id, children, sectionNode.position);
    if (!layout) return;
    batchUpdateNodeParents(layout.positionUpdates);
    canvas.updateNodePosition(id, layout.sectionPosition);
    canvas.updateNodeDimensions(id, {
      width: layout.sectionWidth,
      height: layout.sectionHeight,
    });
    requestAnimationFrame(() => applySectionMembership(id));
  }, [id, getNodes, batchUpdateNodeParents, canvas, applySectionMembership]);

  const handleLayoutMode = useCallback(
    (mode: SectionLayoutMode) => {
      const children = getNodes().filter((n) => n.parentId === id);
      const layout = computeSectionLayoutByMode(id, children, mode);
      if (!layout) return;
      batchUpdateNodeParents(layout.positionUpdates);
      canvas.updateNodeDimensions(id, {
        width: layout.sectionWidth,
        height: layout.sectionHeight,
      });
      requestAnimationFrame(() => applySectionMembership(id));
    },
    [id, getNodes, batchUpdateNodeParents, canvas, applySectionMembership],
  );

  const handleChatSubmit = useCallback(
    async (message: string) => {
      const sectionEntity: ReferencedEntity = {
        entity_id: id,
        entity_type: "canvas_section",
        name: data?.title || "Section",
        platform: "cohesive",
      };
      const flowSessionId = await dispatchProcess({
        nodeId: id,
        referencedEntities: [sectionEntity, ...childEntities],
        action: "chat",
        customPrompt: message,
      });
      setShowChatInput(false);
      if (flowSessionId && canvasId) {
        openCanvasFloatingSession(flowSessionId);
      }
    },
    [
      id,
      data?.title,
      childEntities,
      dispatchProcess,
      canvasId,
      openCanvasFloatingSession,
    ],
  );

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Clear chat + popover latches on multi-select or deselect — otherwise
  // showChatInput / isPopoverOpen alone would remount the wrong UI when
  // returning to single-select (node stays selected during multi-select).
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
      if (isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  const { zoom } = useViewport();
  const { frameWidth, frameHeight } = resolveSectionFrameDimensions({
    width,
    height,
    data,
  });

  const pillHidden = zoom < MIN_PILL_ZOOM;
  const availableTitleScreenWidth = Math.max(
    0,
    frameWidth * zoom * PILL_NODE_WIDTH_RATIO - PILL_CHROME_PX,
  );
  const effectiveMaxTitleWidth = Math.min(
    TITLE_DEFAULT_MAX_PX,
    availableTitleScreenWidth,
  );
  const effectiveMinTitleWidth = Math.min(40, availableTitleScreenWidth);

  // Toolbar follows selection only — do not keep it mounted via isPopoverOpen /
  // showChatInput after deselection.
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  const accentColor = getSectionBorderColor(sectionColor);
  const bodyBg = `color-mix(in srgb, ${sectionColor} ${noFill ? "5%" : "95%"}, transparent)`;
  const borderColor = selected ? "hsl(var(--primary))" : accentColor;

  return (
    <div
      className={cn(
        "relative",
        (isPreviewTarget || isSectionPreviewMember) &&
          "ring-2 ring-canvas-selection rounded-lg",
      )}
      style={{ width: frameWidth, height: frameHeight }}
    >
      {/* Off-screen mirror — keeps titleWidth accurate when the pill is hidden by zoom. */}
      <span
        ref={titleMeasureRef}
        aria-hidden="true"
        style={{
          position: "fixed",
          top: -9999,
          left: -9999,
          visibility: "hidden",
          pointerEvents: "none",
          whiteSpace: "pre",
          fontSize: "1rem",
          fontWeight: 500,
          fontFamily: "inherit",
        }}
      >
        {localTitle || "A"}
      </span>

      {!pillHidden && (
        <div
          className="absolute left-0 nowheel pointer-events-auto z-10 nodrag"
          style={{
            bottom: `calc(100% + ${8 / zoom}px)`,
            transform: `scale(${1 / zoom})`,
            transformOrigin: "bottom left",
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="inline-flex flex-col rounded-lg bg-neutral-0/95 shadow-sm border">
            <div className="inline-flex items-center gap-0.5 pl-2.5 pr-2.5 py-1">
              <input
                ref={titleInputRef}
                type="text"
                value={localTitle}
                readOnly={!isEditingTitle || !canEdit}
                onChange={(e) => setLocalTitle(e.target.value)}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                onFocus={() => {
                  if (canEdit) setIsEditingTitle(true);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canEdit && !isEditingTitle) handleStartEditingTitle();
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  if (canEdit) handleStartEditingTitle();
                }}
                className={cn(
                  "bg-transparent outline-none border-none text-foreground text-base font-medium nodrag",
                  canEdit ? "cursor-text" : "cursor-default select-none",
                )}
                style={{
                  width: titleWidth + 4,
                  minWidth: effectiveMinTitleWidth,
                  maxWidth: effectiveMaxTitleWidth,
                  overflow: "hidden",
                  textOverflow: isEditingTitle ? "clip" : "ellipsis",
                  whiteSpace: "nowrap",
                }}
              />
            </div>
          </div>
        </div>
      )}

      <AnimatePresence>
        {showToolbar && (
          <CanvasFloatingToolbarPortal
            positionAbsoluteX={positionAbsoluteX}
            positionAbsoluteY={positionAbsoluteY}
            width={frameWidth}
            topOffset={pillHidden ? 58 : 98}
            onPointerDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toolbarMouseDownRef.current = true;
              window.addEventListener(
                "pointerup",
                () => {
                  toolbarMouseDownRef.current = false;
                },
                { once: true, capture: true },
              );
            }}
          >
            {showChatInput ? (
              <BaseEntityCardQuickChat
                visible={true}
                onSubmit={handleChatSubmit}
                onClose={() => setShowChatInput(false)}
              />
            ) : (
              <SectionFloatingToolbar
                sectionColor={sectionColor}
                noFill={noFill}
                borderStyle={borderStyle}
                borderWidth={borderWidth}
                onRename={handleStartEditingTitle}
                onColorChange={handleColorChange}
                onNoFillChange={handleNoFillChange}
                onBorderStyleChange={handleBorderStyleChange}
                onBorderWidthChange={handleBorderWidthChange}
                onLayoutMode={handleLayoutMode}
                onResizeToFit={handleResizeToFit}
                onPopoverOpenChange={handlePopoverOpenChange}
                onChatClick={() => setShowChatInput(true)}
              />
            )}
          </CanvasFloatingToolbarPortal>
        )}
      </AnimatePresence>

      {selected && canEdit && (
        <NodeResizer
          isVisible={selected}
          minWidth={MIN_SECTION_SIZE}
          minHeight={MIN_SECTION_SIZE}
          onResizeStart={(_evt, params) => {
            isResizingRef.current = true;
            resizeOriginRef.current = { x: params.x, y: params.y };
            updateSectionResizePreview(params);
          }}
          onResize={(_evt, params) => {
            updateSectionResizePreview(params);
          }}
          onResizeEnd={(_evt, params) => {
            isResizingRef.current = false;
            setSectionPreview(null);
            const snap = (v: number) => snapToGrid(v, LATTICE);
            const w = snap(params.width);
            const h = snap(params.height);
            const origin = resizeOriginRef.current;
            const posUpdates: { x?: number; y?: number } = {};
            if (origin) {
              if (params.x !== origin.x) posUpdates.x = snap(params.x);
              if (params.y !== origin.y) posUpdates.y = snap(params.y);
            }
            canvas.updateNodeDimensions(id, { width: w, height: h });
            if (Object.keys(posUpdates).length > 0) {
              const node = getNodes().find((n) => n.id === id);
              if (node) {
                canvas.updateNodePosition(id, {
                  x: posUpdates.x ?? node.position.x,
                  y: posUpdates.y ?? node.position.y,
                });
              }
            }
            resizeOriginRef.current = null;
            requestAnimationFrame(() => applySectionMembership(id));
          }}
        />
      )}

      <div
        className="w-full h-full rounded-lg box-border pointer-events-none"
        style={{
          backgroundColor: bodyBg,
          borderWidth,
          borderStyle,
          borderColor,
        }}
      />
    </div>
  );
});
